import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';
import { Banner, EmptyState, ErrorState, Loading } from './states';

interface IpRule {
  id: string;
  cidr: string;
  description: string;
}

/**
 * The tenant's control-plane IP allowlist. Only a tenant admin sees it. When it has
 * any entries, the control plane is reachable only from those networks — for every
 * session and API token, not just at login — so a stolen credential replayed from
 * elsewhere is refused. The backend will not let a rule lock out the editor's own
 * address, but the warning here makes the risk plain before saving.
 */
export function IpAllowlistPanel(): JSX.Element {
  const t = useT();
  const { can } = useAuth();
  const { data, error, loading, reload } = useAsync(
    () => (can('tenant:admin') ? api.get<IpRule[]>('/auth/ip-rules') : Promise.resolve([])),
    [],
  );
  const [cidr, setCidr] = useState('');
  const [description, setDescription] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  if (!can('tenant:admin')) return <></>;

  const add = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError(null);
    try {
      await api.post('/auth/ip-rules', { cidr, description });
      setCidr('');
      setDescription('');
      reload();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    }
  };

  const remove = async (id: string): Promise<void> => {
    setFormError(null);
    try {
      await api.del(`/auth/ip-rules/${id}`);
      reload();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    }
  };

  return (
    <div className="card">
      <h2>{t('ipAllowlist.title')}</h2>
      <p>{t('ipAllowlist.intro')}</p>
      {formError ? <Banner variant="error">{formError}</Banner> : null}

      <form className="form form--inline" onSubmit={(e) => void add(e)}>
        <label className="field">
          <span>{t('ipAllowlist.cidr')}</span>
          <input
            placeholder="203.0.113.0/24"
            value={cidr}
            onChange={(e) => setCidr(e.target.value)}
          />
        </label>
        <label className="field">
          <span>{t('ipAllowlist.description')}</span>
          <input value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <button type="submit" className="btn">
          {t('ipAllowlist.add')}
        </button>
      </form>

      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorState message={error.message} onRetry={reload} />
      ) : !data || data.length === 0 ? (
        <EmptyState hint={t('ipAllowlist.openHint')} />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('ipAllowlist.cidr')}</th>
                <th>{t('ipAllowlist.description')}</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {data.map((rule) => (
                <tr key={rule.id}>
                  <td>
                    <code>{rule.cidr}</code>
                  </td>
                  <td>{rule.description || '—'}</td>
                  <td className="row-actions">
                    <button
                      type="button"
                      className="btn btn--ghost btn--danger"
                      onClick={() => void remove(rule.id)}
                    >
                      {t('ipAllowlist.remove')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
